import { notifications } from '@mantine/notifications';
import type {
  Lang,
  MailBatchCounts,
  MailBatchItemStatus,
  MailBatchStatus,
  MailKind,
  MailTemplateBody,
  MailTemplateKey,
  PlaceholderKey,
  TemplateCheck,
} from '@reditelji/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, ApiRequestError } from './client';
import { useSession } from './session';

export interface MailTemplateRow {
  key: MailTemplateKey;
  sl: MailTemplateBody;
  en: MailTemplateBody;
  updatedAt: string;
  updatedById: string | null;
  updatedByName: string;
}

export interface MailSendState {
  volunteerId: string;
  templateKey: MailTemplateKey;
  at: string;
  by: string;
}

export interface MailPreview {
  subject: string;
  html: string;
  text: string;
  check: TemplateCheck;
  unresolved: string[];
  blocked: string | null;
  /** Whom a shared mailbox's mail was rendered for; empty for one person. */
  people: string[];
}

/** One other person on the addressed volunteer's mailbox (skupni predal). */
export interface HouseholdMember {
  id: string;
  name: string;
  /** Why the template refuses them, or null. */
  blocked: string | null;
  /** Owed this mail — what ticks them from the start. */
  due: boolean;
  /** In the rendered mail this payload carries. */
  included: boolean;
}

export interface MailReview {
  lang: Lang;
  to: string | null;
  template: MailTemplateBody;
  subject: string;
  html: string;
  text: string;
  unresolved: string[];
  blocked: string | null;
  household: HouseholdMember[];
  /** Who the rendered mail goes to, by name. */
  recipients: { id: string; name: string }[];
}

export interface MemberOutcome {
  volunteerId: string;
  name: string;
  skipped?: string;
}

export interface MailBatch {
  id: string;
  templateKey: MailKind;
  /** An obvestilo's subject — what names it in a list; null for a template. */
  subject: string | null;
  status: MailBatchStatus;
  counts: MailBatchCounts;
  createdById: string | null;
  createdByName: string;
  createdAt: string;
  finishedAt: string | null;
  /** False when the batch was drained inside the request (jobs disabled). */
  queued?: boolean;
  items: {
    volunteerId: string;
    volunteerName: string;
    status: MailBatchItemStatus;
    reason: string;
    /** Who else was in the same mail — the people of a shared mailbox. */
    sentWith: string[];
  }[];
}

function onError(err: Error) {
  notifications.show({
    color: 'red',
    title: 'Napaka',
    message:
      err instanceof ApiRequestError && err.status === 409
        ? 'Predlogo je medtem uredil nekdo drug. Osveži in poskusi znova.'
        : err.message,
  });
}

export function useMailTemplates() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'templates'],
    queryFn: () => api<MailTemplateRow[]>('/mail-templates'),
    enabled: can('comms:write'),
  });
}

export function useMailSends() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'sends'],
    queryFn: () => api<MailSendState[]>('/mail-templates/sends'),
    enabled: can('comms:read'),
  });
}

/** One folder the ingest reads, and how its last pass over it went. */
export interface IngestFolder {
  path: string;
  direction: 'in' | 'out';
  lastRunAt: string;
  lastOkAt: string | null;
  failures: number;
  lastError: string;
  lastIngested: number;
  mailCount: number;
  heldCount: number;
}

export interface IngestStatus {
  configured: boolean;
  folders: IngestFolder[];
  triage: number;
}

/**
 * The poll runs every minute, so the view is worth refreshing while it is
 * open — the question it answers is "is this working *now*".
 */
export function useIngestStatus() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'ingest-status'],
    queryFn: () => api<IngestStatus>('/messages/ingest-status'),
    enabled: can('comms:write'),
    refetchInterval: 30_000,
  });
}

/**
 * The preview is the server's own render, debounced against the draft — the
 * same code path the send uses, so the two cannot drift.
 */
export function useMailPreview() {
  return useMutation({
    mutationFn: (input: {
      key: MailTemplateKey;
      lang: Lang;
      subject: string;
      body: string;
      volunteerId: string | null;
      /** The household members ticked in the review dialog; absent renders the whole household. */
      with?: string[];
      /** With no volunteer: the sample as a mail to two people. */
      household?: boolean;
    }) =>
      api<MailPreview>(`/mail-templates/${input.key}/preview`, {
        method: 'POST',
        body: JSON.stringify({
          lang: input.lang,
          subject: input.subject,
          body: input.body,
          volunteerId: input.volunteerId,
          ...(input.with ? { with: input.with } : {}),
          ...(input.household ? { household: true } : {}),
        }),
      }),
    onError,
  });
}

export function useMailMutations() {
  const qc = useQueryClient();
  const invalidateSends = () => {
    void qc.invalidateQueries({ queryKey: ['mail', 'sends'] });
    // A new batch has to show up in the recent list immediately — that list is
    // the only way back to a send whose dialog has been closed.
    void qc.invalidateQueries({ queryKey: ['mail', 'batches'] });
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
  };

  return {
    save: useMutation({
      mutationFn: (input: {
        key: MailTemplateKey;
        sl: MailTemplateBody;
        en: MailTemplateBody;
        updatedAt: string;
      }) =>
        api<MailTemplateRow>(`/mail-templates/${input.key}`, {
          method: 'PATCH',
          body: JSON.stringify({ sl: input.sl, en: input.en, updatedAt: input.updatedAt }),
        }),
      onError,
      // The stored row is the form's baseline; putting it in place at once
      // settles "Shrani" with the toast, and a second save carries its version.
      onSuccess: (saved) => {
        qc.setQueryData<MailTemplateRow[]>(['mail', 'templates'], (rows) =>
          rows?.map((r) => (r.key === saved.key ? saved : r)),
        );
        void qc.invalidateQueries({ queryKey: ['mail', 'templates'] });
      },
    }),
    reset: useMutation({
      mutationFn: (input: { key: MailTemplateKey; updatedAt: string }) =>
        api<MailTemplateRow>(`/mail-templates/${input.key}/reset`, {
          method: 'POST',
          body: JSON.stringify({ updatedAt: input.updatedAt }),
        }),
      onError,
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['mail', 'templates'] }),
    }),
    translate: useMutation({
      mutationFn: (input: { key: MailTemplateKey; subject: string; body: string }) =>
        api<MailTemplateBody>(`/mail-templates/${input.key}/translate`, {
          method: 'POST',
          body: JSON.stringify({ subject: input.subject, body: input.body }),
        }),
      onError,
    }),
    testSend: useMutation({
      mutationFn: (input: {
        key: MailTemplateKey;
        lang: Lang;
        subject: string;
        body: string;
        volunteerId: string | null;
        /** With no volunteer: the sample as a mail to two people. */
        household?: boolean;
      }) =>
        api<{ ok: true; to: string }>(`/mail-templates/${input.key}/test-send`, {
          method: 'POST',
          body: JSON.stringify({
            lang: input.lang,
            subject: input.subject,
            body: input.body,
            volunteerId: input.volunteerId,
            ...(input.household ? { household: true } : {}),
          }),
        }),
      onError,
      onSuccess: (res) =>
        notifications.show({ title: 'Poslano', message: `Poskusno sporočilo je na ${res.to}.` }),
    }),
    sendOne: useMutation({
      mutationFn: (input: {
        volunteerId: string;
        key: MailTemplateKey;
        override?: MailTemplateBody;
        /** Household members to include in the same mail. */
        with?: string[];
      }) =>
        api<{ messageId: string; subject?: string; members: MemberOutcome[] }>(
          `/volunteers/${input.volunteerId}/mail/${input.key}`,
          {
            method: 'POST',
            body: JSON.stringify({ override: input.override, with: input.with ?? [] }),
          },
        ),
      onError,
      onSettled: invalidateSends,
    }),
    // Returns as soon as the batch exists; the mails follow in a job. The
    // caller watches `useMailBatch` for progress and the final report.
    sendBulk: useMutation({
      mutationFn: (input: { key: MailTemplateKey; volunteerIds: string[] }) =>
        api<MailBatch>(`/mail-templates/${input.key}/send`, {
          method: 'POST',
          body: JSON.stringify({ volunteerIds: input.volunteerIds }),
        }),
      onError,
      onSettled: invalidateSends,
    }),
  };
}

/**
 * One bulk send, polled while it runs.
 *
 * A batch of 150 takes minutes, so the dialog follows it rather than waiting on
 * a request. Polling stops the moment the batch reports `done`, and the send
 * states are refreshed then so the matrix behind the dialog catches up.
 */
export function useMailBatch(batchId: string | null) {
  const qc = useQueryClient();
  const { can } = useSession();
  const query = useQuery({
    queryKey: ['mail', 'batch', batchId],
    queryFn: () => api<MailBatch>(`/mail-batches/${batchId!}`),
    enabled: batchId !== null && can('comms:write'),
    refetchInterval: (q) => (q.state.data?.status === 'done' ? false : 1500),
  });

  const done = query.data?.status === 'done';
  useEffect(() => {
    if (!done) return;
    void qc.invalidateQueries({ queryKey: ['mail', 'sends'] });
    void qc.invalidateQueries({ queryKey: ['mail', 'batches'] });
    void qc.invalidateQueries({ queryKey: ['mail', 'notices'] });
    void qc.invalidateQueries({ queryKey: ['mail', 'notice'] });
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
  }, [done, qc]);

  return query;
}

/**
 * Recent bulk sends, so one stays findable after its dialog is closed.
 *
 * Polls only while something is still running — the list is otherwise static,
 * and a coordinator watching a send from here should see it move.
 */
export function useRecentBatches(limit = 8) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'batches', limit],
    queryFn: () => api<MailBatch[]>(`/mail-batches?limit=${limit}`),
    enabled: can('comms:write'),
    refetchInterval: (q) =>
      (q.state.data ?? []).some((b) => b.status !== 'done') ? 2000 : false,
  });
}

/**
 * The mail as it would go out, for the review dialog — rendered with the
 * household members in `withIds`, and carrying the whole household so the
 * dialog can offer the rest.
 */
export function useMailReview(
  volunteerId: string | null,
  key: MailTemplateKey | null,
  withIds: readonly string[] = [],
) {
  const withParam = [...withIds].sort().join(',');
  return useQuery({
    queryKey: ['mail', 'review', volunteerId, key, withParam],
    queryFn: () =>
      api<MailReview>(
        `/volunteers/${volunteerId!}/mail/${key!}${withParam ? `?with=${encodeURIComponent(withParam)}` : ''}`,
      ),
    enabled: volunteerId !== null && key !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Obvestila — one-off notices (SPEC §1.1H)
// ---------------------------------------------------------------------------

/** Both languages of an obvestilo, as written. */
export type NoticeBodies = Record<Lang, MailTemplateBody>;

/** One obvestilo in the history list: counted, without its recipients. */
export interface MailNoticeSummary {
  id: string;
  subject: string;
  status: MailBatchStatus;
  counts: MailBatchCounts;
  createdById: string | null;
  createdByName: string;
  createdAt: string;
  finishedAt: string | null;
}

/** One obvestilo read back: what it said and what became of each recipient. */
export interface MailNoticeRecord extends MailBatch {
  notice: NoticeBodies & { includeCancelled: boolean };
}

/** What a preview or a test send of the draft is asked for. */
export interface NoticeRenderInput {
  lang: Lang;
  subject: string;
  body: string;
  volunteerId: string | null;
  /** The picked housemates to render with; always sent, `[]` for somebody mailed alone. */
  with: string[];
  household: boolean;
  includeCancelled: boolean;
}

const renderBody = (input: NoticeRenderInput) =>
  JSON.stringify({
    lang: input.lang,
    subject: input.subject,
    body: input.body,
    volunteerId: input.volunteerId,
    // Only with a person picked: `with` names who shares their mail.
    ...(input.volunteerId ? { with: input.with } : {}),
    household: input.volunteerId === null && input.household,
    includeCancelled: input.includeCancelled,
  });

export function useNoticePreview() {
  return useMutation({
    mutationFn: (input: NoticeRenderInput) =>
      api<MailPreview>('/mail-notices/preview', { method: 'POST', body: renderBody(input) }),
    onError,
  });
}

export function useNoticeMutations(opts: { onSent?: () => void } = {}) {
  const qc = useQueryClient();
  const { onSent } = opts;
  return {
    translate: useMutation({
      mutationFn: (input: MailTemplateBody) =>
        api<MailTemplateBody>('/mail-notices/translate', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      onError,
    }),
    testSend: useMutation({
      mutationFn: (input: NoticeRenderInput) =>
        api<{ ok: true; to: string }>('/mail-notices/test-send', {
          method: 'POST',
          body: renderBody(input),
        }),
      onError,
      onSuccess: (res) =>
        notifications.show({ title: 'Poslano', message: `Poskusno sporočilo je na ${res.to}.` }),
    }),
    // Returns as soon as the batch exists; `useMailBatch` follows the send.
    send: useMutation({
      mutationFn: (input: NoticeBodies & { volunteerIds: string[]; includeCancelled: boolean }) =>
        api<MailBatch>('/mail-notices', { method: 'POST', body: JSON.stringify(input) }),
      onError,
      // Here rather than in `mutate`'s own callbacks: those are dropped when the
      // component that called it unmounts, and an accepted send whose draft
      // survives is one click from being sent twice.
      onSuccess: () => onSent?.(),
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: ['mail', 'notices'] });
        void qc.invalidateQueries({ queryKey: ['mail', 'batches'] });
        void qc.invalidateQueries({ queryKey: ['messages'] });
        void qc.invalidateQueries({ queryKey: ['changelog'] });
      },
    }),
  };
}

/** Every obvestilo sent, newest first — polled while one is still going out. */
export function useMailNotices() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'notices'],
    queryFn: () => api<MailNoticeSummary[]>('/mail-notices'),
    enabled: can('comms:write'),
    refetchInterval: (q) =>
      (q.state.data ?? []).some((n) => n.status !== 'done') ? 2000 : false,
  });
}

export function useMailNotice(id: string | null) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'notice', id],
    queryFn: () => api<MailNoticeRecord>(`/mail-notices/${id!}`),
    enabled: id !== null && can('comms:write'),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'done' ? 1500 : false),
  });
}

export type { MailTemplateKey, PlaceholderKey };

// ---------------------------------------------------------------------------
// Dnevno sporočilo
// ---------------------------------------------------------------------------

export interface DayMailDay {
  id: string;
  date: string;
  label: string;
  /** Whether "Pošlji zdaj" may send it: today, or any day where mail reaches a catcher. */
  mayStart: boolean;
}

export interface DayMailState {
  auto: boolean;
  /** Who the 03:00 mail is signed by — whoever turned the switch on. */
  signedBy: string | null;
  today: string;
  days: DayMailDay[];
  day: DayMailDay;
  plan: {
    afterMin: number;
    recipients: { id: string; name: string; shifts: { postCode: string; fromMin: number; toMin: number }[] }[];
    skipped: { id: string; name: string; reason: string }[];
  };
  runs: {
    id: string;
    trigger: 'auto' | 'manual';
    outcome: 'batch' | 'nobody';
    createdAt: string;
    createdBy: string | null;
    batch: MailBatch | null;
  }[];
}

export function useDayMail(eventDayId: string | null) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['mail', 'day-mail', eventDayId],
    queryFn: () => api<DayMailState>(`/mail/day-mail${eventDayId ? `?eventDayId=${eventDayId}` : ''}`),
    enabled: can('comms:read'),
    // A run under way reports as it goes.
    refetchInterval: (q) =>
      q.state.data?.runs.some((r) => r.batch !== null && r.batch.status !== 'done') ? 3000 : false,
  });
}

export function useDayMailMutations() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['mail'] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
  };
  return {
    setAuto: useMutation({
      mutationFn: (on: boolean) =>
        api<{ auto: boolean; missedToday: boolean }>('/mail/day-mail/auto', {
          method: 'PUT',
          body: JSON.stringify({ on }),
        }),
      onError,
      onSuccess: (res) => {
        if (res.missedToday) {
          notifications.show({
            color: 'yellow',
            title: 'Današnje sporočilo ne pride samo',
            message:
              'Samodejno pošiljanje danes ne doseže več vseh — kdor sporočila še ni prejel, ga dobi s »Pošlji zdaj«.',
          });
        }
      },
      onSettled: refresh,
    }),
    sendNow: useMutation({
      mutationFn: (eventDayId: string) =>
        api<{ status: 'started' | 'nobody'; recipients?: number }>('/mail/day-mail/send', {
          method: 'POST',
          body: JSON.stringify({ eventDayId }),
        }),
      onError,
      onSuccess: (res) =>
        notifications.show({
          title: res.status === 'started' ? 'Pošiljanje se je začelo' : 'Ni komu poslati',
          message:
            res.status === 'started'
              ? `Dnevno sporočilo gre ${res.recipients} ${res.recipients === 1 ? 'osebi' : 'osebam'}.`
              : 'Vsi s potrjeno izmeno so ga že prejeli.',
        }),
      onSettled: refresh,
    }),
  };
}
